import React, { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
// Components
import { NonIdealState, H6, Icon } from '@blueprintjs/core';
import ParameterForm from './ParameterForm';
import Button, { ButtonSize } from 'src/components/button/Button';
import Collapse from 'src/components/collapse/Collapse';
// Types
import {
  CreateConnectorFormData,
  EConnectorAuthParams,
  IConnectorRequestParameter,
} from 'src/types/internal-Types';

interface IParametersListProps {
  values: IConnectorRequestParameter[];
  path: string;
}

const ParametersList: FC<IParametersListProps> = ({ values, path }) => {
  const { setFieldValue } = useFormikContext<CreateConnectorFormData>();

  const { t } = useTranslation(['connectors']);

  const handleAdd = useCallback(() => {
    const currentValue = [...values];
    currentValue.push({
      name: '',
      in: EConnectorAuthParams.path,
      required: true,
      schema: {
        type: 'string',
      },
    });
    setFieldValue(`${path}.parameters`, currentValue);
  }, [setFieldValue, values, path]);

  const handleRemove = useCallback(
    (index: number) => {
      const currentValue = [...values];
      currentValue.splice(index, 1);
      setFieldValue(`${path}.parameters`, currentValue);
    },
    [setFieldValue, values, path]
  );

  return (
    <Collapse header={<H6>{t('titles.Request parameters')}</H6>}>
      <div className="parameters-list">
        {values?.length ? (
          values.map((data, index) => (
            <ParameterForm
              onRemove={handleRemove}
              path={`${path}.parameters.${index}`}
              data={data}
              key={index}
              index={index}
            />
          ))
        ) : (
          <NonIdealState
            icon={<Icon icon="error" iconSize={40} />}
            description={t('No parameters') as string}
          />
        )}
      </div>
      <div className="parameters-list-footer">
        <Button onClick={handleAdd} size={ButtonSize.small}>
          {t('buttons.New parameter')}
        </Button>
      </div>
    </Collapse>
  );
};

export default ParametersList;
